import { useEffect, useState, useRef } from "react";
import AdminApiModule from "../../components/AdminApi/Api/api";
import { BASE_URL } from "../../components/config/config";
import axios from "axios";
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Paper,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Divider,
  Avatar,
} from "@mui/material";
import {
  Warning as WarningIcon,
  LocationOn as LocationIcon,
  Watch as WatchIcon,
  AttachMoney as CashIcon,
  CurrencyExchange as CurrencyIcon,
  Payment as OnlinePaymentIcon,
  CameraAlt as CameraIcon,
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Pause as PauseIcon,
  SentimentVerySatisfied as HappyCodeIcon,
  Notes as ManualRemarkIcon,
} from "@mui/icons-material";
import { styled } from "@mui/system";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { format } from "date-fns";
import { useSelector } from "react-redux";

const StyledModal = styled(Modal)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const ModalContent = styled(Paper)({
  padding: "20px",
  width: "400px",
  outline: "none",
  maxHeight: "80vh",
  overflowY: "auto",
});

const PaymentOption = styled(Card)(({ selected }) => ({
  cursor: "pointer",
  border: selected ? "2px solid #00796A" : "1px solid #ccc",
  backgroundColor: selected ? "#00796A20" : "#fff",
  "&:hover": {
    borderColor: "#00796A",
  },
}));

const PictureBox = styled(Box)({
  width: "100px",
  height: "100px",
  border: "1px dashed #ccc",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  "&:hover": {
    borderColor: "#00796A",
  },
});

const JobCompletedModal = ({ open, onClose, ticketId }) => {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [paymentType, setPaymentType] = useState("Online");
  const [serialNumber, setSerialNumber] = useState("");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [status, setStatus] = useState("IN PROGRESS");
  const [isLoading, setIsLoading] = useState(false);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [doesTicketExists, setDoesTicketExists] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const fileInputRef = useRef(null);
  const [dateOfPurchase, setDateOfPurchase] = useState(null);
  const [remarks, setRemarks] = useState(
    "Visit Done, Work Completed (Payment Collected)"
  );
  const [manualRemark, setManualRemark] = useState("");
  const [happyCode, setHappyCode] = useState("");
  const [ticketClosingTotalAmount, setTicketClosingTotalAmount] = useState("");
  const [
    ticketClosingPlatformShareAmount,
    setTicketClosingPlatformShareAmount,
  ] = useState("");
  const [amounts, setAmounts] = useState("");
  const [rating, setRating] = useState("Good");
  const [imageUploadURL, setImageUploadURL] = useState({});

  const K_OPTIONS = [
    "Visit Done, Work Completed (Payment Collected)",
    "Visited - Estimate Not Approved / Site Not Ready",
    "Visited - Unit not Repairable /Special Skill Required",
    "Not Visited - Can't Deliver Service",
    "Visit Done, Work Completed (Free of Charge)",
  ];

  const REVIEW_OPTIONS = ["Very Bad", "Bad", "Decent", "Good", "Very Good"];

  const jobComplete = async () => {
    setLoading(true);
    try {
      // Validate required fields
      if (!doesTicketExists && uploadedImages.length === 0) {
        showSnackbar("Upload Onsite Pictures!", "error");
        return;
      }

      let fileUploadResponse = {};
      // Upload images if needed
      if (uploadedImages.length > 0) {
        fileUploadResponse = await uploadImagesToServer(ticketId);
      }

      // Prepare payload - use the actual ticket price from API response
      const baseTicketPrice = ticketDetails?.ticket_price || 0;
      const additionalCharges = Number(amounts) || 0;
      const closingTicketPrice = baseTicketPrice + additionalCharges;

      const payload = {
        closing_ticket_price: closingTicketPrice,
        ticket_status: "CLOSED",
        onsite_pictures:
          fileUploadResponse?.fileSavedUrl || imageUploadURL?.fileSavedUrl,
        center_message: manualRemark,
        take_time: new Date().toString(),
        code: happyCode,
        ticket_closing_total_amount: ticketClosingTotalAmount,
        ticket_closing_platform_share_amount: ticketClosingPlatformShareAmount,
        remarks: {
          close_ticket_remarks: remarks,
          technician_remarks: manualRemark,
          admin_remarks: "",
          message: "",
        },
        payment_type: paymentType,
        ticket_obj_id: ticketId,
        date_of_purchase: dateOfPurchase
          ? format(dateOfPurchase, "yyyy-MM-dd")
          : null,
        // Add other required fields
        service_available: ticketDetails?.service_available,
        assigned_ids: {
          assigned_center_id:
            ticketDetails?.assigned_ids?.assigned_center_id?._id,
        },
      };

      await axios.post(`${BASE_URL}admin/close-ticket-by-admin`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      showSnackbar("Job marked as completed successfully!", "success");
      onClose();
    } catch (error) {
      console.error("Job completion error:", error);
      const message =
        error?.response?.data?.message || "Failed to complete job";
      showSnackbar(message, "error");
    } finally {
      setLoading(false);
    }
  };

  const getAllTickets = (ticketId) => {
    startLoading();
    let payload = {
      ticket_obj_id: ticketId,
    };

    new AdminApiModule()
      .getSingleTicket(payload)
      .then((res) => {
        if (res?.data?.results?.length) {
          setDoesTicketExists(true);
          // CORRECTED: Ticket data is in res.data.results[0]
          const ticketData = res?.data?.results?.[0];
          setTicketDetails(ticketData);

          // Map API data to component state with proper fallbacks
          setStatus(ticketData?.ticket_status || "IN PROGRESS");
          setRemarks(
            ticketData?.remarks?.close_ticket_remarks ||
              "Visit Done, Work Completed (Payment Collected)"
          );
          setPaymentType(ticketData?.mode_of_payment || "Online");
          setHappyCode(ticketData?.code?.happy_code || "");

          // Set amounts from ticket data
          const ticketPrice = ticketData?.ticket_price || 0;
          setAmounts(ticketPrice.toString());

          // Set platform share amount if available
          if (ticketData?.final_settlement_amount) {
            setTicketClosingPlatformShareAmount(
              ticketData.final_settlement_amount
            );
          }

          // Calculate total closing amount
          if (ticketPrice > 0) {
            setTicketClosingTotalAmount(ticketPrice.toString());
          }

          // Set serial number from API if available
          if (ticketData?.serial_number) {
            setSerialNumber(ticketData.serial_number);
          }

          // Set date of purchase if available
          if (ticketData?.date_of_purchase) {
            setDateOfPurchase(new Date(ticketData.date_of_purchase));
          }

          // Set manual remarks if available
          if (ticketData?.remarks?.technician_remarks) {
            setManualRemark(ticketData.remarks.technician_remarks);
          }
        }
        stopLoading();
      })
      .catch((err) => {
        stopLoading();
        console.error("Error fetching ticket:", err);
        showSnackbar("Failed to fetch ticket details", "error");
      });
  };

  const startLoading = () => {
    setIsLoading(true);
  };

  const stopLoading = () => {
    setIsLoading(false);
  };

  const handleImageUpload = (event) => {
    const files = event.target.files;
    if (files?.length) {
      const newImages = Array.from(files).map((file) => ({
        name: file.name,
        url: URL.createObjectURL(file),
        file: file,
      }));
      setUploadedImages((prev) => [...prev, ...newImages]);
      setUploadModalOpen(false);
    }
  };

  const uploadImagesToServer = async (ticketId) => {
    const ticketIdToUse = ticketId;
    if (!ticketIdToUse) return;

    const formData = new FormData();
    uploadedImages.forEach((image) => {
      formData.append("OnsitePicture", image.file);
    });

    try {
      const response = await axios.post(
        `${BASE_URL}technicianApp/upload-ticket-onsite-picture/${ticketIdToUse}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setImageUploadURL(response.data?.data);
      return response.data?.data;
    } catch (error) {
      console.error("Image upload error:", error);
      throw error;
    }
  };

  useEffect(() => {
    if (ticketId && open) {
      getAllTickets(ticketId);
    }
  }, [ticketId, open]);

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Helper function to get full address
  const getFullAddress = () => {
    if (!ticketDetails?.address_details) return "";

    const addr = ticketDetails.address_details;
    return `${addr.house_number || ""}, ${addr.locality || ""}, ${
      addr.city || ""
    }, ${addr.state || ""}, ${addr.pincode || ""}`;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <StyledModal open={open} onClose={onClose}>
        <ModalContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Button onClick={onClose} startIcon={<ArrowBackIcon />} />
            <Typography variant="h6" sx={{ ml: 1 }}>
              Request ID: {ticketDetails?.ticket_id || "Loading..."}
            </Typography>
          </Box>

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : ticketDetails ? (
            <>
              <Paper
                elevation={1}
                sx={{ p: 2, mb: 2, backgroundColor: "#f5f5f5" }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <WarningIcon color="warning" sx={{ fontSize: 40, mr: 2 }} />
                  <Box>
                    <Typography variant="subtitle1">Request For:</Typography>
                    <Typography variant="h8">
                      {ticketDetails?.service_provided_for?.service_name ||
                        "Service not specified"}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <LocationIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="body1" color="primary">
                  {getFullAddress()}
                </Typography>
              </Box>

              {/* Status Section */}
              <Paper
                elevation={1}
                sx={{ p: 2, mb: 2, backgroundColor: "#f5f5f5" }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <WatchIcon sx={{ fontSize: 40, mr: 2 }} />
                  <Box>
                    <Typography variant="subtitle1">Status:</Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {ticketDetails?.ticket_status || "UNKNOWN"}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Onsite Pictures Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Onsite Pictures (Upload Only 1 Picture)
                </Typography>
                <Grid container spacing={2}>
                  {/* Show uploaded images */}
                  {uploadedImages.map((image, index) => (
                    <Grid item key={index}>
                      <Avatar
                        src={image.url}
                        variant="square"
                        sx={{ width: 100, height: 100 }}
                      />
                    </Grid>
                  ))}
                  {/* Upload button */}
                  <Grid item>
                    <PictureBox onClick={() => setUploadModalOpen(true)}>
                      <CameraIcon fontSize="large" color="action" />
                    </PictureBox>
                  </Grid>
                </Grid>
              </Box>

              {/* Due Amount Section */}
              <Paper
                elevation={1}
                sx={{ p: 2, mb: 2, backgroundColor: "#f5f5f5" }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CashIcon sx={{ fontSize: 40, mr: 2 }} />
                  <Box>
                    <Typography variant="subtitle1">Ticket Price</Typography>
                    <Typography variant="h6" fontWeight="bold">
                      ₹{ticketDetails?.ticket_price || 0}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Additional Charges Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <CashIcon sx={{ fontSize: 40, mr: 2 }} />
                  <Typography variant="subtitle1" sx={{ mr: 2 }}>
                    Material Charges (Input Cost)
                  </Typography>
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Amount"
                    value={amounts}
                    onChange={(e) => setAmounts(e.target.value)}
                    type="number"
                    InputProps={{
                      startAdornment: (
                        <CurrencyIcon color="action" sx={{ mr: 1 }} />
                      ),
                    }}
                    sx={{ width: "150px" }}
                  />
                </Box>

                <Typography variant="subtitle2" gutterBottom>
                  Payment Method
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <PaymentOption
                      selected={paymentType === "Online"}
                      onClick={() => setPaymentType("Online")}
                    >
                      <CardContent sx={{ textAlign: "center" }}>
                        <OnlinePaymentIcon fontSize="large" />
                        <Typography>Online</Typography>
                      </CardContent>
                    </PaymentOption>
                  </Grid>
                  <Grid item xs={6}>
                    <PaymentOption
                      selected={paymentType === "Cash"}
                      onClick={() => setPaymentType("Cash")}
                    >
                      <CardContent sx={{ textAlign: "center" }}>
                        <CashIcon fontSize="large" />
                        <Typography>Cash</Typography>
                      </CardContent>
                    </PaymentOption>
                  </Grid>
                </Grid>
              </Box>

              {/* Vendor Payout */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <ManualRemarkIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1" gutterBottom>
                    Vendor Payout
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  placeholder="Enter Vendor Payout Amount"
                  value={ticketClosingPlatformShareAmount}
                  onChange={(e) =>
                    setTicketClosingPlatformShareAmount(e.target.value)
                  }
                />
              </Box>

              {/* Total Closing Amount */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <ManualRemarkIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1" gutterBottom>
                    Total Closing Amount
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  placeholder="Enter Total Closing Amount"
                  value={ticketClosingTotalAmount}
                  onChange={(e) => setTicketClosingTotalAmount(e.target.value)}
                />
              </Box>

              {/* Serial Number Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <ManualRemarkIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1" gutterBottom>
                    Serial Number
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Enter Serial Number..."
                  value={serialNumber}
                  onChange={(e) => setSerialNumber(e.target.value)}
                />
              </Box>

              {/* Date of Purchase Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Date of Purchase
                  </Typography>
                </Box>

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={dateOfPurchase}
                    onChange={(newValue) => setDateOfPurchase(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        variant="outlined"
                        placeholder="Select Date of Purchase"
                      />
                    )}
                  />
                </LocalizationProvider>
              </Box>

              {/* Rate Customer Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Rate Customer
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={rating}
                    onChange={(e) => setRating(e.target.value)}
                    displayEmpty
                  >
                    {REVIEW_OPTIONS.map((item, index) => (
                      <MenuItem key={index} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Closure Remark Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Closure Remark
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    displayEmpty
                  >
                    {K_OPTIONS.map((item, index) => (
                      <MenuItem key={index} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Manual Remark Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <ManualRemarkIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1" gutterBottom>
                    Manual Remark
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  placeholder="Enter any additional remarks..."
                  value={manualRemark}
                  onChange={(e) => setManualRemark(e.target.value)}
                />
              </Box>

              {/* Happy Code Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <HappyCodeIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1" gutterBottom>
                    Happy Code
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Enter happy code if applicable"
                  value={happyCode}
                  onChange={(e) => setHappyCode(e.target.value)}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 3,
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<PauseIcon />}
                  onClick={() => {
                    setStatus("ON HOLD");
                    showSnackbar("Ticket put on hold!", "info");
                  }}
                  sx={{ width: "48%" }}
                >
                  Put on Hold
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CheckIcon />}
                  onClick={jobComplete}
                  disabled={loading}
                  sx={{ width: "48%" }}
                >
                  {loading ? <CircularProgress size={24} /> : "Job Completed"}
                </Button>
              </Box>
            </>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <Typography>No ticket data found</Typography>
            </Box>
          )}
        </ModalContent>
      </StyledModal>

      {/* Upload Picture Modal */}
      <StyledModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
      >
        <ModalContent>
          <Typography variant="h6" gutterBottom>
            Upload Picture
          </Typography>

          {/* Hidden file input */}
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleImageUpload}
            multiple
          />

          <Button
            variant="contained"
            fullWidth
            startIcon={<CameraIcon />}
            onClick={() => fileInputRef.current.click()}
            sx={{ mb: 2 }}
          >
            Upload Picture
          </Button>
          <Typography variant="body2" color="text.secondary">
            You can upload multiple images at once
          </Typography>
        </ModalContent>
      </StyledModal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  );
};

export default JobCompletedModal;
